import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store, createSelector } from '@ngrx/store';
import dayjs from 'dayjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { take, takeUntil, Subject } from 'rxjs';

import { FreyaCommonModule } from '../../../../freya-common/freya-common.module';
import { AvailabilityDateComponent } from '../../../../new-opportunity/new-opportunity-customer/availability-date/availability-date.component';
import { quickJsActions } from '../../../../quickjs/quickjs.actions';
import { HighlightDatesDirective } from '../../../../shared/directives/highlight-dates.directive';
import { DiscardOpportunityDialogActions } from '../../../job-create/discard-job-component/discard-job.actions';
import { OverviewTimelineActions } from '../../../job-overview/overview-timeline/overview-timeline.actions';
import { overviewTimelineFeature } from '../../../job-overview/overview-timeline/overview-timeline.feature';
import { selectTimelineHighlightDates } from '../../../job-overview/overview-timeline/overview-timeline.selectors';
import { JobToolActions } from '../../../job-tool.actions';

export type HoldState = 'idle' | 'applied' | 'suggesting' | 'no-discount';

export interface SuggestionDay {
    date: string;
    formattedDate: string;
    tierName: string;
    tierPercent: number;
    tierColor: string;
    isCurrentDate?: boolean;
}

const NEARBY_DAYS_RANGE = 3;
const MAX_SUGGESTIONS = 6;

@Component({
    selector: 'app-calculator-date-picker',
    standalone: true,
    imports: [
        CommonModule,
        FreyaCommonModule,
        HighlightDatesDirective,
        AvailabilityDateComponent,
        DialogModule,
        ButtonModule,
    ],
    templateUrl: './calculator-date-picker.component.html',
    styleUrl: './calculator-date-picker.component.scss',
})
export class CalculatorDatePickerComponent implements OnInit, OnDestroy {

    /**
     * Emits the first selected date as a YYYY-MM-DD string whenever the
     * user picks a date. The parent dispatches inputChanged({ key, value }).
     */
    @Output() dateSelected = new EventEmitter<string>();

    public actions = OverviewTimelineActions;

    // ---- Dialog state ----
    holdState: HoldState = 'idle';
    showSuggestionDialog = false;
    suggestionDays: SuggestionDay[] = [];

    /** Tier info for the currently applied discount (set when holdState==='applied') */
    appliedTierName = '';
    appliedTierPercent = 0;

    /** Current date info shown at the top of the dialog */
    currentDayInfo: SuggestionDay | null = null;

    /** Current applied discount percent (0 = none applied yet). Tracks partial steps. */
    currentAppliedPercent = 0;

    // ---- Store selectors ----
    viewModel$ = this.store.select(createSelector({
        selectedDate: createSelector(
            overviewTimelineFeature.selectTimeline,
            (timeline) => (timeline as any[]).length > 0 ? dayjs((timeline as any[])[0]).toDate() : null,
        ),
        timelineDates: createSelector(
            overviewTimelineFeature.selectTimeline,
            (timeline): string[] => timeline as string[],
        ),
        highlightDates: selectTimelineHighlightDates,
        // Expose the full viewDatesMap so we can scan ±3 days for better tiers
        viewDatesMap: createSelector(
            overviewTimelineFeature.selectJobV3OverviewTimelineState,
            (state: any): Record<string, any> => state?.viewDatesMap ?? {},
        ),
    }));

    private readonly _destroy$ = new Subject<void>();

    constructor(
        public store: Store,
        private actions$: Actions,
    ) {}

    ngOnInit(): void {
        // Dispatch monthChanged to force a fresh viewDates (prophet:true) fetch.
        this.store.select(overviewTimelineFeature.selectTimeline).pipe(take(1)).subscribe((timeline: any) => {
            const ref = (Array.isArray(timeline) && timeline.length > 0)
                ? dayjs(timeline[0])
                : dayjs();
            this.store.dispatch(this.actions.monthChanged({ month: ref.month() + 1, year: ref.year() }));
        });

        // Reset the hold button visual state whenever the job is discarded or reset.
        // The QuickJS reducer already clears userInputs on these actions — this ensures
        // the component's local UI state (progress bar, shake, applied label) also resets.
        this.actions$.pipe(
            ofType(
                DiscardOpportunityDialogActions.discardUnsavedChangesButtonClicked,
                JobToolActions.jobDataReset,
                JobToolActions.jobLoaded,
                JobToolActions.saveChangesSuccess,
            ),
            takeUntil(this._destroy$),
        ).subscribe(() => {
            this._resetDiscountState();
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // -------------------------------------------------------------------------
    // Date selection
    // -------------------------------------------------------------------------

    onDateSelected(date: Date | null): void {
        if (!date) { return; }
        const dateStr = dayjs(date).format('YYYY-MM-DD');
        this.store.dispatch(this.actions.timelineDatesSelected({ timeline: [dateStr] }));
        this.dateSelected.emit(dateStr);
        // Reset discount state when date changes
        this._resetDiscountState();
        this.store.dispatch(quickJsActions.inputChanged({ key: 'discountApplied', value: false }));
        this.store.dispatch(quickJsActions.inputChanged({ key: 'discountPercent', value: 0 }));
        this.store.dispatch(quickJsActions.inputChanged({ key: 'dynamicPricingDate', value: null }));
    }

    onMonthChanged(event: { month: number; year: number }): void {
        this.store.dispatch(this.actions.monthChanged({ month: event.month, year: event.year }));
    }

    // -------------------------------------------------------------------------
    // Ask for Discount
    // -------------------------------------------------------------------------

    askForDiscount(): void {
        this.viewModel$.pipe(take(1)).subscribe((vm) => {
            const selectedDateStr = vm.timelineDates[0];
            if (!selectedDateStr) { return; }

            const selectedEntry = vm.viewDatesMap[selectedDateStr];
            const selectedTierPercent: number = selectedEntry?.prophet?.discount?.tier?.percent ?? 0;

            const currentDayInfo: SuggestionDay = {
                date: selectedDateStr,
                formattedDate: dayjs(selectedDateStr).format('ddd, MMM D'),
                tierName: selectedEntry?.prophet?.discount?.tier?.name ?? 'Standard',
                tierPercent: selectedTierPercent,
                tierColor: selectedEntry?.prophet?.discount?.tier?.color ?? '#6b7280',
                isCurrentDate: true,
            };
            this.currentDayInfo = currentDayInfo;

            const betterDays = this._findNearbyDays(vm.viewDatesMap, selectedDateStr, selectedTierPercent);
            this.suggestionDays = betterDays;
            this.holdState = 'suggesting';
            this.showSuggestionDialog = true;
        });
    }

    // -------------------------------------------------------------------------
    // Discount application
    // -------------------------------------------------------------------------

    applyDiscountFromDialog(day: SuggestionDay): void {
        if (day.isCurrentDate) {
            this.appliedTierName = day.tierName;
            this.appliedTierPercent = day.tierPercent;
            this.currentAppliedPercent = day.tierPercent;
            this.holdState = 'applied';
            this.showSuggestionDialog = false;
            this.store.dispatch(quickJsActions.inputChanged({ key: 'discountPercent', value: day.tierPercent }));
            this.store.dispatch(quickJsActions.inputChanged({ key: 'discountApplied', value: true }));
        } else {
            this.onSuggestionDatePicked(day);
        }
    }

    resetDiscount(): void {
        this._resetDiscountState();
        this.store.dispatch(quickJsActions.inputChanged({ key: 'discountApplied', value: false }));
        this.store.dispatch(quickJsActions.inputChanged({ key: 'discountPercent', value: 0 }));
        this.store.dispatch(quickJsActions.inputChanged({ key: 'dynamicPricingDate', value: null }));
    }

    private _resetDiscountState(): void {
        this.holdState = 'idle';
        this.appliedTierName = '';
        this.appliedTierPercent = 0;
        this.currentAppliedPercent = 0;
        this.showSuggestionDialog = false;
        this.suggestionDays = [];
        this.currentDayInfo = null;
    }

    // -------------------------------------------------------------------------
    // Nearby day suggestion
    // -------------------------------------------------------------------------

    private _findNearbyDays(
        viewDatesMap: Record<string, any>,
        selectedDateStr: string,
        _selectedTierPercent: number,
    ): SuggestionDay[] {
        const selected = dayjs(selectedDateStr);
        const results: SuggestionDay[] = [];

        for (let offset = -NEARBY_DAYS_RANGE; offset <= NEARBY_DAYS_RANGE; offset++) {
            if (offset === 0) { continue; }
            const dateStr = selected.add(offset, 'day').format('YYYY-MM-DD');
            const entry = viewDatesMap[dateStr];
            if (!entry) { continue; }
            const tier = entry.prophet?.discount?.tier;
            if (!tier || typeof tier.percent !== 'number') { continue; }
            results.push({
                date: dateStr,
                formattedDate: dayjs(dateStr).format('ddd, MMM D'),
                tierName: tier.name,
                tierPercent: tier.percent,
                tierColor: tier.color ?? '#3b82f6',
            });
        }

        return results
            .sort((a, b) => a.tierPercent - b.tierPercent)
            .slice(0, MAX_SUGGESTIONS);
    }

    onSuggestionDatePicked(day: SuggestionDay): void {
        this.showSuggestionDialog = false;
        this.store.dispatch(this.actions.timelineDatesSelected({ timeline: [day.date] }));
        this.dateSelected.emit(day.date);
        this.appliedTierName = day.tierName;
        this.appliedTierPercent = day.tierPercent;
        this.currentAppliedPercent = day.tierPercent;
        this.holdState = 'applied';
        this.store.dispatch(quickJsActions.inputChanged({ key: 'dynamicPricingDate', value: day.date }));
        this.store.dispatch(quickJsActions.inputChanged({ key: 'discountPercent', value: day.tierPercent }));
        this.store.dispatch(quickJsActions.inputChanged({ key: 'discountApplied', value: true }));
    }

    onKeepCurrentDate(): void {
        this.showSuggestionDialog = false;
        this.holdState = 'idle';
    }

    onDialogHide(): void {
        if (this.holdState === 'suggesting') {
            this.onKeepCurrentDate();
        }
    }
}
